import { trackAddToCart } from './analytics';

const BASE = process.env.REACT_APP_BACKEND_URL;

// ---- session id (cart linkage) ----
export const getSessionId = () => {
  let sid = localStorage.getItem('eletrovex_session_id');
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) ||
          `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('eletrovex_session_id', sid);
  }
  return sid;
};

const handle = async (res) => {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    const err = new Error(`API ${res.status}: ${body}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

// Normalize product fields between backend (snake_case) and legacy frontend code (camelCase)
const normalizeProduct = (p) => {
  if (!p) return p;
  return {
    ...p,
    originalPrice: p.originalPrice ?? p.original_price ?? null,
    price: typeof p.price === 'number' ? p.price : Number(p.price || 0),
  };
};

// ---- PRODUCTS / CATEGORIES / BANNERS (deduped + short TTL cache, seeded by build-time snapshot) ----
const SNAPSHOT = (typeof window !== 'undefined' && window.__ELETROVEX__) || {};
const TTL_MS = 60 * 1000;
const cache = {};
const inflight = {};

const cachedGet = (key, url, transform = (x) => x) => {
  const hit = cache[key];
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.data);
  if (inflight[key]) return inflight[key];
  inflight[key] = fetch(url).then(handle).then(transform).then((data) => {
    cache[key] = { data, at: Date.now() };
    return data;
  }).finally(() => { delete inflight[key]; });
  return inflight[key];
};

/* Data embedded in index.html at build time — lets pages render before the API answers. */
export const preloaded = (key) => (Array.isArray(SNAPSHOT[key]) ? SNAPSHOT[key] : null);
export const preloadedProducts = () => { const p = preloaded('products'); return p ? p.map(normalizeProduct) : null; };

export const fetchProducts = (category) => {
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  return cachedGet(`products${qs}`, `${BASE}/api/products${qs}`, (arr) => Array.isArray(arr) ? arr.map(normalizeProduct) : arr);
};
export const fetchProduct = (id) => {
  const fromList = cache.products?.data?.find?.((p) => p.id === id);
  if (fromList && Date.now() - cache.products.at < TTL_MS) return Promise.resolve(fromList);
  return fetch(`${BASE}/api/products/${encodeURIComponent(id)}`).then(handle).then(normalizeProduct);
};
export const fetchCategories = () => cachedGet('categories', `${BASE}/api/categories`);
export const fetchBanners = () => cachedGet('banners', `${BASE}/api/banners`);
export const invalidateCatalogCache = () => { Object.keys(cache).forEach((k) => delete cache[k]); };

// ---- CART ----
export const fetchCart = () => fetch(`${BASE}/api/cart/${getSessionId()}`).then(handle);
export const addToCartApi = (item) =>
  fetch(`${BASE}/api/cart`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...item, session_id: getSessionId() })
  }).then(handle).then((res) => { trackAddToCart(item); return res; });
export const updateCartItem = (itemId, quantity) =>
  fetch(`${BASE}/api/cart/item/${itemId}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ quantity })
  }).then(handle);
export const removeCartItem = (itemId) =>
  fetch(`${BASE}/api/cart/item/${itemId}`, { method: 'DELETE' }).then(handle);
export const clearCart = () =>
  fetch(`${BASE}/api/cart/${getSessionId()}`, { method: 'DELETE' }).then(handle);

// ---- CHECKOUT ----
export const fetchCheckoutConfig = () => fetch(`${BASE}/api/checkout/config`).then(handle);
export const createMpPreference = ({ shipping_service_id, ...address }) =>
  fetch(`${BASE}/api/checkout/mercadopago`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: getSessionId(), address, shipping_service_id: shipping_service_id || null })
  }).then(handle);

// ---- ORDERS ----
// X-Session-Id allows backend to release PII to the legitimate buyer
export const fetchOrder = (externalReference) =>
  fetch(`${BASE}/api/orders/${encodeURIComponent(externalReference)}`, {
    headers: { 'X-Session-Id': getSessionId() }
  }).then(handle);

// ---- SHIPPING (Melhor Envio) ----
export const fetchShippingConfig = () => fetch(`${BASE}/api/shipping/config`).then(handle);
export const trackOrder = (q) => fetch(`${BASE}/api/track?q=${encodeURIComponent(q)}`).then(handle);
export const quoteShipping = (payload) =>
  fetch(`${BASE}/api/shipping/quote`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).then(handle);
export const adminBuyShippingLabel = (ref) => adminFetch(`/api/admin/orders/${encodeURIComponent(ref)}/shipping/buy`, { method: 'POST' }).then(handle);
export const adminRefreshTracking = (ref) => adminFetch(`/api/admin/orders/${encodeURIComponent(ref)}/shipping/tracking`, { method: 'POST' }).then(handle);
export const adminMarkShipped = (ref, payload = {}) =>
  adminFetch(`/api/admin/orders/${encodeURIComponent(ref)}/ship`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).then(handle);
export const adminMarkDelivered = (ref) =>
  adminFetch(`/api/admin/orders/${encodeURIComponent(ref)}/deliver`, { method: 'POST' }).then(handle);
export const fetchOfficialLabelPdf = (ref) =>
  adminFetch(`/api/admin/orders/${encodeURIComponent(ref)}/shipping/label.pdf`).then(async (res) => {
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text().catch(() => '')}`);
    return res.blob();
  });


// ---- CONTACT (Fale Conosco → e-mail para o dono) ----
export const sendContactMessage = (payload) =>
  fetch(`${BASE}/api/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(async (res) => {
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const detail = Array.isArray(data.detail) ? data.detail.map((d) => d.msg).join(', ') : data.detail;
      throw new Error(detail || `Erro ${res.status}`);
    }
    return res.json();
  });


// ---- ADMIN (auth via Bearer token + httpOnly cookie fallback) ----
const TOKEN_KEY = 'eletrovex_admin_token';
export const getAdminToken = () => localStorage.getItem(TOKEN_KEY) || '';
export const setAdminToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

const adminFetch = (path, init = {}) => {
  const headers = { ...(init.headers || {}) };
  const token = getAdminToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (init.method && init.method !== 'GET') invalidateCatalogCache();
  return fetch(`${BASE}${path}`, { credentials: 'include', ...init, headers });
};

export const adminLogin = (email, password) =>
  adminFetch('/api/auth/login', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  }).then(handle).then((res) => {
    if (res && res.token) setAdminToken(res.token);
    return res;
  });
export const adminLogout = () =>
  adminFetch('/api/auth/logout', { method: 'POST' })
    .then(handle)
    .finally(() => setAdminToken(''));
export const adminMe = () => adminFetch('/api/auth/me').then(handle);
export const fetchAdminOrders = () => adminFetch('/api/admin/orders?limit=50').then(handle);
export const fetchOrderLabelPdf = (ref) =>
  adminFetch(`/api/admin/orders/${encodeURIComponent(ref)}/label.pdf`).then(async (res) => {
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text().catch(() => '')}`);
    return res.blob();
  });

// ---- ADMIN PRODUCTS ----
export const adminCreateProduct = (product) =>
  adminFetch('/api/admin/products', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(product),
  }).then(handle);
export const adminUpdateProduct = (id, product) =>
  adminFetch(`/api/admin/products/${encodeURIComponent(id)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(product),
  }).then(handle);
export const adminDeleteProduct = (id) =>
  adminFetch(`/api/admin/products/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(handle);

// ---- ADMIN VIDEO UPLOAD (chunked) ----
const VIDEO_CHUNK = 4 * 1024 * 1024;
export const adminUploadVideo = async (file, onProgress = () => {}) => {
  const { upload_id } = await adminFetch('/api/admin/videos/upload/init', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, content_type: file.type || 'video/mp4', size: file.size }),
  }).then(handle);
  const total = Math.ceil(file.size / VIDEO_CHUNK);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append('index', String(i));
    fd.append('file', file.slice(i * VIDEO_CHUNK, (i + 1) * VIDEO_CHUNK));
    await adminFetch(`/api/admin/videos/upload/${upload_id}/chunk`, { method: 'POST', body: fd }).then(handle);
    onProgress(Math.round(((i + 1) / total) * 90));
  }
  const res = await adminFetch(`/api/admin/videos/upload/${upload_id}/complete`, { method: 'POST' }).then(handle);
  onProgress(100);
  return res;
};

// ---- ADMIN UPLOAD ----
export const adminUploadImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = (reader.result || '').toString();
      adminFetch('/api/admin/upload', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type || 'image/jpeg',
          data: b64,
        }),
      }).then(handle).then(resolve).catch(reject);
    };
    reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
    reader.readAsDataURL(file);
  });

// ---- ADMIN BANNERS ----
export const adminListBanners = () => adminFetch('/api/admin/banners').then(handle);
export const adminCreateBanner = (banner) =>
  adminFetch('/api/admin/banners', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(banner),
  }).then(handle);
export const adminUpdateBanner = (id, banner) =>
  adminFetch(`/api/admin/banners/${encodeURIComponent(id)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(banner),
  }).then(handle);
export const adminDeleteBanner = (id) =>
  adminFetch(`/api/admin/banners/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(handle);

// ---- ADMIN CATEGORIES ----
export const adminListCategories = () => adminFetch('/api/admin/categories').then(handle);
export const adminCreateCategory = (cat) =>
  adminFetch('/api/admin/categories', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cat),
  }).then(handle);
export const adminUpdateCategory = (id, cat) =>
  adminFetch(`/api/admin/categories/${encodeURIComponent(id)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cat),
  }).then(handle);
export const adminDeleteCategory = (id) =>
  adminFetch(`/api/admin/categories/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(handle);
export const adminReorderCategories = (ids) =>
  adminFetch('/api/admin/categories/reorder', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  }).then(handle);

// ---- ADMIN BLING ----
export const blingStatus = () => adminFetch('/api/bling/status').then(handle);
export const blingAuthorize = () => adminFetch('/api/bling/authorize').then(handle);
export const blingDisconnect = () => adminFetch('/api/bling/disconnect', { method: 'POST' }).then(handle);
export const blingRefresh = () => adminFetch('/api/bling/refresh', { method: 'POST' }).then(handle);
export const blingSendOrder = (ref) =>
  adminFetch(`/api/bling/orders/${encodeURIComponent(ref)}/send`, { method: 'POST' }).then(handle);
export const blingEmitNfe = (ref) =>
  adminFetch(`/api/bling/orders/${encodeURIComponent(ref)}/nfe`, { method: 'POST' }).then(handle);
export const blingGenLabel = (ref) =>
  adminFetch(`/api/bling/orders/${encodeURIComponent(ref)}/label`, { method: 'POST' }).then(handle);
